import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

export const DEVICE_DETAILS_PAGE = 'AD';
export const CLOUD_SOURCE_DETAILS_PAGE = 'CS';

export const useDeviceSelectionParams = () => {
  const [searchParams] = useSearchParams();

  return useMemo(() => {
    const deviceUid = searchParams.get('duid') || '';
    const sourceUid = searchParams.get('suid') || '';
    const page = searchParams.get('pg') || DEVICE_DETAILS_PAGE;
    const hasDeviceSelection = deviceUid.length > 0;
    const hasSourceSelection = sourceUid.length > 0;

    return {
      deviceUid,
      sourceUid,
      page,
      hasDeviceSelection,
      hasSourceSelection,
      hasDetailSelection: hasDeviceSelection || hasSourceSelection,
      isDeviceDetailsView: hasDeviceSelection && !hasSourceSelection,
      isSourceDetailsView: hasSourceSelection
    };
  }, [searchParams]);
};

const useDeviceQueryParam = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { deviceUid, sourceUid, page } = useDeviceSelectionParams();

  const updateSearchParams = useCallback(
    (
      updates: Record<string, string>,
      {
        clearSearchFilters = false,
        replace = false
      }: { clearSearchFilters?: boolean; replace?: boolean } = {}
    ) => {
      const nextParams = new URLSearchParams(location.search);

      if (clearSearchFilters) {
        nextParams.delete('q');
      }

      Object.entries(updates).forEach(([key, value]) => {
        if (value) {
          nextParams.set(key, value);
          return;
        }

        nextParams.delete(key);
      });

      const currentSearch = location.search.startsWith('?')
        ? location.search.slice(1)
        : location.search;
      const nextSearch = nextParams.toString();

      if (nextSearch === currentSearch) {
        return;
      }

      navigate(
        {
          pathname: location.pathname,
          search: nextSearch ? `?${nextSearch}` : '',
          hash: location.hash
        },
        { replace }
      );
    },
    [location.hash, location.pathname, location.search, navigate]
  );

  const clearParams = useCallback(
    ({ replace = false }: { replace?: boolean } = {}) => {
      if (!location.search) {
        return;
      }

      navigate(
        {
          pathname: location.pathname,
          search: '',
          hash: location.hash
        },
        { replace }
      );
    },
    [location.hash, location.pathname, location.search, navigate]
  );

  const addPageFilter = useCallback(
    (nextPage: string, options?: { replace?: boolean }) => {
      updateSearchParams(
        {
          pg: nextPage,
          ...(deviceUid && { duid: deviceUid }),
          ...(sourceUid && { suid: sourceUid })
        },
        options
      );
    },
    [updateSearchParams, deviceUid, sourceUid]
  );

  const addDeviceFilter = useCallback(
    (device_uid: string) => {
      updateSearchParams({
        pg: DEVICE_DETAILS_PAGE,
        duid: device_uid,
        suid: ''
      });
    },
    [updateSearchParams]
  );

  const addSourceFilter = useCallback(
    (device_uid: string, source_uid: string) => {
      updateSearchParams({
        pg: CLOUD_SOURCE_DETAILS_PAGE,
        duid: device_uid,
        suid: source_uid
      });
    },
    [updateSearchParams]
  );

  const removeFilter = useCallback(() => {
    updateSearchParams({
      duid: '',
      suid: '',
      pg: page || DEVICE_DETAILS_PAGE
    });
  }, [page, updateSearchParams]);

  return {
    addDeviceFilter,
    addSourceFilter,
    addPageFilter,
    removeFilter,
    clearParams,
    updateSearchParams,
    deviceUid,
    sourceUid,
    page
  };
};

export default useDeviceQueryParam;
